<template>
  <div
    v-if="currentStep"
    :class="['period-range', { 'period-range_disabled': disabled }]"
  >
    <div
      v-if="isMobile"
      class="period-range__text period-range__text_title period-range__text_title_mobile"
    >
      {{ title }}
    </div>

    <!-- LABEL AND CONTROLS -->
    <div
      :class="[
        'period-range__label-container',
        { 'period-range__label-container_bottom': currentStepIndex % 2 === 1 }
      ]"
    >
      <div
        v-if="isMobile"
        :class="[
          'period-range__control',
          'period-range__control_decrease',
          { 'period-range__control_disabled': !prevStep }
        ]"
        @click="selectStep(prevStep)"
      >
        –
      </div>

      <div
        class="period-range__label"
        :style="labelStyle"
      >
        {{ label }}
      </div>

      <div
        v-if="isMobile"
        :class="[
          'period-range__control',
          'period-range__control_increase',
          { 'period-range__control_disabled': !nextStep }
        ]"
        @click="selectStep(nextStep)"
      >
        +
      </div>
    </div>
    <!-- LABEL AND CONTROLS -->

    <!-- LINE -->
    <div class="period-range__line">
      <div
        v-for="(step, index) in steps"
        :key="`step-${index}`"
        :class="[
          'period-range__dot',
          { 'period-range__dot_active': step.value === currentStep.value }
        ]"
        :data-text="`${step.distance} / ${step.period}`"
        @click="selectStep(step, $event)"
      />
    </div>
    <!-- LINE -->

    <!-- TEXT UNDERLINE -->
    <div class="period-range__text-container">
      <div v-if="isMobile" class="period-range__text period-range__text_min period-range__text_min_mobile">
        <div class="text_min_mobile__bottom">{{ steps[0].distance }}</div>
        <div class="text_min_mobile__top">{{ steps[0].period }}</div>
      </div>

      <div v-if="isMobile" class="period-range__text period-range__text_min period-range__text_min_mobile">
        <div class="text_max_mobile__bottom">{{ steps[steps.length - 1].distance }}</div>
        <div class="text_max_mobile__top">{{ steps[steps.length - 1].period }}</div>
      </div>
    </div>
    <!-- TEXT UNDERLINE -->
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  export default {
    name: 'PeriodRange',
    props: {
      disabled: {
        type: Boolean,
        default: false
      },
      steps: {
        type: Array,
        required: true,
        validator (steps) {
          return steps.every(step => {
            return 'period' in step &&
              'distance' in step &&
              'value' in step
          })
        }
      },
      title: {
        type: String,
        required: true
      },
      value: {
        type: String,
        default: ''
      }
    },
    data () {
      return {
        currentStep: null,
        isMounted: false,
        labelXPos: '',
        labelIsReady: false
      }
    },
    computed: {
      ...mapGetters(['isTablet', 'isMobile']),
      label () {
        return this.getFullLabel(this.currentStep)
      },
      labelStyle () {
        return this.isMobile
          ? null
          : { transform: `translateX(${this.labelXPos})` }
      },
      prevStep () {
        return this.steps[this.currentStepIndex - 1]
      },
      nextStep () {
        return this.steps[this.currentStepIndex + 1]
      },
      currentStepIndex () {
        return this.steps.findIndex(step => step.value === this.currentStep.value)
      }
    },
    created () {
      this.selectStep(this.steps.find(step => step.value === this.value) || this.steps[0])
    },
    activated () {
      window.addEventListener('resize', this.moveLabel)
    },
    mounted () {
      window.addEventListener('resize', this.moveLabel)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.moveLabel)
    },
    deactivated () {
      window.removeEventListener('resize', this.moveLabel)
    },
    methods: {
      selectStep (step) {
        if (!step) return

        this.currentStep = step
        this.$emit('input', step.value)
        this.moveLabel()
      },
      getFullLabel (step) {
        return `${step.distance} / ${step.period}`
      },
      moveLabel () {
        // move label horizontaly according to selected dot
        this.$nextTick(() => {
          const activeDotElement = document.querySelector('.period-range__dot_active')
          const label = document.querySelector('.period-range__label')
          const isFirstDot = this.currentStepIndex === 0
          const isLastDot = this.currentStepIndex === this.steps.length - 1

          if (this.isTablet && isFirstDot) {
            this.labelXPos = `${-25 + activeDotElement.offsetLeft}px`
          } else if (this.isTablet && isLastDot) {
            this.labelXPos = `${75 + activeDotElement.offsetLeft - label.offsetWidth}px`
          } else {
            this.labelXPos = `${22 + activeDotElement.offsetLeft - label.offsetWidth / 2}px`
          }
        })
      }
    }
  }
</script>

<style lang="scss">
  .period-range {
    position: relative;

    &__label {
      &-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        transition: transform 0.3s cubic-bezier(0.17, 0.67, 0.55, 1.1);

        &_bottom {
          transform: translateY(74px);

          @include tablet {
            transform: translateY(63px);
          }

          @include mobile {
            transform: translateY(0);
          }
        }
      }

      position: relative;
      z-index: 2;
      display: inline-block;
      padding: 1em 1.3em;
      font-size: 14px;
      line-height: 1em;
      background-color: $c-gray-light;
      border-radius: 1.5em;
      transition: transform 0.3s cubic-bezier(0.17, 0.67, 0.55, 1.1);

      @include tablet {
        padding: 0.9em;
        font-size: 11px;
      }
    }

    &__control {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      background-color: $c-gray-light;
      border-radius: 50%;
      transition: opacity 0.3s;

      &_disabled {
        opacity: 0.5;
      }

      &_decrease {
        padding-bottom: 1px;
      }
    }

    &__line {
      display: flex;
      justify-content: space-between;
      height: 8px;
      padding: 0 20px;
      margin: 12px auto;
      background-color: $c-gray-light;
      border-radius: 6px;

      @include tablet {
        padding: 0 35px;
      }

      @include mobile {
        padding: 0 10px;
        margin-bottom: 15px;
      }
    }

    &__dot {
      position: relative;
      width: 0;
      height: 0;
      padding: 30px 20px;
      margin: -30px -20px;
      cursor: pointer;
      transform: translateY(4px);

      @include mobile {
        padding: 20px;
        margin: -20px;
      }

      // dot
      &::before {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 10px;
        height: 10px;
        content: "";
        background-color: $c-gray-medium;
        border-radius: 50%;
        transition: transform 0.2s cubic-bezier(0.17, 0.67, 0.55, 1.5), background-color 0.3s linear;
        transform: translate(-50%, -50%);
      }

      @include hover {
        &::before {
          background-color: darken($c-gray-medium, 10);
        }
      }

      // text
      &::after {
        position: absolute;
        left: 50%;
        font-size: 11px;
        color: $c-gray-dark;
        white-space: nowrap;
        content: attr(data-text);
        transition: 0.3s;
        transform: translateX(-50%);

        @include mobile {
          display: none;
        }
      }

      &:nth-child(odd) {
        &::after {
          top: 0;
        }
      }

      &:nth-child(even) {
        &::after {
          bottom: 0;
        }
      }

      &_active {
        &::before {
          background-color: $c-active-light !important;
          transform: translate(-50%, -50%) scale(1.6);
        }

        &::after {
          opacity: 0;
        }
      }
    }

    &__text {
      &-container {
        display: flex;
        justify-content: space-between;
      }

      font-size: 12px;
      color: $c-gray-dark;

      &_title {
        &_mobile {
          margin-bottom: 20px;
        }
      }
    }

    .text {
      &_min_mobile,
      &_max_mobile {
        &__top,
        &__bottom {
          font-size: 12px;
          color: $c-gray-dark;
        }

        &__top {
          margin-top: 10px;
        }

        &__bottom {
          padding-bottom: 10px;
          border-bottom: 1px solid currentColor;
        }
      }

      &_max_mobile {
        &__top,
        &__bottom {
          text-align: right;
        }
      }
    }

    &_disabled {
      pointer-events: none;

      .period-range__control {
        visibility: hidden;
      }
    }
  }
</style>
